/**
 * MANAGEMENT (14) / ONBOARDING-REQUESTS — the read/write hooks for the LAB-ACCESS-REQUEST review workflow.
 * ADDITIVE to the shell `../api.ts` (which owns `managementKeys` + `useApiQuery`/`useApiMutation` + `api` +
 * `isManagementAbsent`). Every endpoint below is code-verified against
 * server/src/modules/management/onboardingRequests.routes.js — all on the `platformRoute` chain
 * (authenticatePlatform → authorizePlatform("management.onboarding.manage")), NO public surface here.
 *
 *   GET  /management/onboarding/requests            management.onboarding.manage → the intake triage inbox
 *   GET  /management/onboarding/requests/:id        management.onboarding.manage → the request drill-down
 *   POST /management/onboarding/requests/:id/review management.onboarding.manage → demande → en_revue
 *   POST /management/onboarding/requests/:id/approve management.onboarding.manage → provision + link + close
 *   POST /management/onboarding/requests/:id/reject  management.onboarding.manage → record raison_rejet + close
 *
 * CROSS-LAB, NOT lab-scoped — the request keys hang off the platform `["platform","management",…]` root so a
 * lab switch never touches them (the funnel is a global platform-plane table, no laboratoire_id). Every
 * transition invalidates the request root AND the shell `managementKeys.root` (an approve provisions a lab, so
 * the lab directory + the Overview KPIs must re-read too).
 */
import { api, cleanQuery, managementKeys, useApiMutation, useApiQuery } from "../api";
import type {
  ApproveRequestResult,
  ApproveRequestVars,
  LabAccessRequestFilters,
  LabAccessRequestPage,
  LabAccessRequestRow,
  RejectRequestVars,
  ReviewRequestVars,
} from "./onboardingRequests.types";

const REQ_BASE = "/management/onboarding/requests";

/** The request-scoped keys — nested under the shell platform root so `managementKeys.root` invalidates them. */
export const onboardingRequestKeys = {
  root: ["platform", "management", "onboarding", "requests"] as const,
  list: (filters: LabAccessRequestFilters = {}) =>
    ["platform", "management", "onboarding", "requests", "list", filters] as const,
  detail: (id: string) => ["platform", "management", "onboarding", "requests", "detail", id] as const,
};

// ── Reads ────────────────────────────────────────────────────────────────────────
/** GET /management/onboarding/requests — the cross-lab intake inbox, filtered + server-paginated. */
export function useLabAccessRequests(filters: LabAccessRequestFilters, enabled: boolean) {
  return useApiQuery<LabAccessRequestPage>(onboardingRequestKeys.list(filters), REQ_BASE, {
    query: cleanQuery(filters),
    enabled,
    staleTime: 15_000,
  });
}

/** GET /management/onboarding/requests/:id — one request's dossier drill-down (the review drawer). */
export function useLabAccessRequest(id: string | null, enabled: boolean) {
  return useApiQuery<LabAccessRequestRow>(
    onboardingRequestKeys.detail(id ?? "—"),
    `${REQ_BASE}/${id ?? ""}`,
    { enabled: enabled && !!id, staleTime: 10_000 },
  );
}

// ── Transitions (correct-by-reissue; each invalidates the whole management estate) ──
/** POST …/:id/review — take a request into triage (demande → en_revue). */
export function useReviewRequest() {
  return useApiMutation<LabAccessRequestRow, ReviewRequestVars>(
    ({ id, ...body }) => api.post<LabAccessRequestRow>(`${REQ_BASE}/${id}/review`, body),
    { invalidate: [onboardingRequestKeys.root, managementKeys.root] },
  );
}

/**
 * POST …/:id/approve — provision a Laboratoire + first owner (the reused onboarding.provisionLab path), then
 * stamp the request approuvee + laboratoire_id. Invalidates the request root AND the shell root so the lab
 * directory + Overview KPIs re-read. A duplicate lab reference / owner email surfaces as a typed 409.
 */
export function useApproveRequest() {
  return useApiMutation<ApproveRequestResult, ApproveRequestVars>(
    ({ id, ...body }) => api.post<ApproveRequestResult>(`${REQ_BASE}/${id}/approve`, body),
    { invalidate: [onboardingRequestKeys.root, managementKeys.root] },
  );
}

/** POST …/:id/reject — record the required raison_rejet + close (demande|en_revue → rejetee). */
export function useRejectRequest() {
  return useApiMutation<LabAccessRequestRow, RejectRequestVars>(
    ({ id, ...body }) => api.post<LabAccessRequestRow>(`${REQ_BASE}/${id}/reject`, body),
    { invalidate: [onboardingRequestKeys.root, managementKeys.root] },
  );
}
